/**
 * Move a demand: to another focus area, or out to the Digital Council (#1292).
 *
 * The reason is required, and required here rather than only server-side —
 * a disabled Save with the field named is a better answer than a round trip
 * that comes back with a refusal. The server still refuses a blank one; this
 * is the fast path, not the rule.
 */

import { ToldLine } from './ToldLine'
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { FOCUS_AREAS } from '../types/focus_area'
import type { Demand } from '../types/demand'
import { useRouteDemand } from '../hooks/useDemands'
import { useDsaiClusters } from '../hooks/useDigitalCouncil'
import { useToast } from './ToastProvider'
import { apiErrorMessage } from '../lib/apiError'

/** The Digital Council is a destination, not a focus area — see the
 *  `disposition` column's comment in models/demand.py for why the two are
 *  kept apart in the record even though the picker offers them together. */
export const DIGITAL_COUNCIL_TARGET = 'DC'

/**
 * How a landing code is WRITTEN. The classifier and the route endpoint still
 * say `DC`; people read "DSAI" (renamed 2026-09-21 — see
 * backend `services/digital_council.py`). Everything else passes through.
 */
export function landingCodeLabel(code: string | null | undefined): string {
  return code === DIGITAL_COUNCIL_TARGET ? 'DSAI' : (code ?? '')
}

const useStyles = makeStyles({
  targets: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  current: { color: tokens.colorNeutralForeground3 },
  suggested: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  context: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorBrandStroke1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: `0 ${tokens.borderRadiusSmall} ${tokens.borderRadiusSmall} 0`,
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase200,
  },
})

export interface RouteDemandDialogProps {
  demand: Demand | null
  onClose: () => void
  /** Who is routing (the effective user). The server does not tell a requester
   *  about their own move; left out, they are assumed to be someone else. */
  actorId?: number | null
  /** Pre-select a target — the "Accept → FA3" path. The reason is still the
   *  submitter of this form's to write; a preset never submits by itself. */
  /** Fired after a successful move — lets the audit queue mark the row
   *  handled so its action buttons don't linger and invite a second move. */
  onRouted?: (demandId: number) => void
}

export function RouteDemandDialog({
  demand,
  onClose,
  onRouted,
  actorId,
}: RouteDemandDialogProps) {
  const styles = useStyles()
  const { showToast } = useToast()
  const route = useRouteDemand()
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')
  const [cluster, setCluster] = useState('')
  const { data: clusters = [] } = useDsaiClusters()
  // The server does not tell a requester about their own move.
  const requesterHears = !!demand?.submitter_user_id && demand.submitter_user_id !== actorId
  const clusterLabel = cluster ? (clusters.find((c) => c.code === cluster)?.label ?? cluster) : ''
  const told =
    target === DIGITAL_COUNCIL_TARGET
      ? [requesterHears && 'the requester', clusterLabel && `the ${clusterLabel} lead`]
          .filter(Boolean)
          .join(', and ')
      : `${target} council (not the requester)`

  // Reset when a different demand is opened, so a reason typed for one demand
  // can never be submitted against another.
  useEffect(() => {
    setTarget('')
    setReason('')
    setCluster('')
  }, [demand?.id])

  if (!demand) return null

  const routedOnward = demand.disposition === 'digital_council'
  // The one piece of context this decision actually uses: what the classifier
  // thought, and whether the submitter went against it. Shown here rather than
  // left in the table, because by the time the dialog is open the table is
  // behind a scrim.
  const suggested = demand.suggested_focus_area
  const canSave = !!target && !!reason.trim() && !route.isPending

  function onSave() {
    if (!canSave || !demand) return
    // Where an undo would put it back: the Digital Council if it was routed
    // there, otherwise the focus area it is leaving.
    const undoTarget = routedOnward ? DIGITAL_COUNCIL_TARGET : demand.focus_area_code
    const demandId = demand.id
    route.mutate(
      {
        id: demandId,
        body: {
          target,
          reason: reason.trim(),
          // Only meaningful for DSAI, and the server refuses it elsewhere.
          ...(target === DIGITAL_COUNCIL_TARGET && cluster ? { dsai_cluster: cluster } : {}),
        },
      },
      {
        onSuccess: () => {
          showToast({
            title:
              target === DIGITAL_COUNCIL_TARGET
                ? 'Routed to DSAI'
                : `Moved to ${target}`,
            // Undo rather than confirm, per the #1245 rule: the move usually
            // records a correct judgement, and a prompt would tax every one of
            // those to guard against the rare slip. Undoing a cross-area move
            // can raise the #995 warning — the interceptor handles it like any
            // other.
            action: {
              label: 'Undo',
              onClick: () =>
                route.mutate(
                  {
                    id: demandId,
                    body: { target: undoTarget, reason: 'Undoing the previous move.' },
                  },
                  {
                    onSuccess: () => showToast({ title: 'Move undone' }),
                    onError: (err) =>
                      showToast({ title: apiErrorMessage(err), intent: 'error' }),
                  },
                ),
            },
          })
          onRouted?.(demandId)
          onClose()
        },
        onError: (err) => showToast({ title: apiErrorMessage(err), intent: 'error' }),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Where does this belong?</DialogTitle>
          <DialogContent>
            <p className={styles.hint} data-testid="route-current">
              “{demand.title}” is in <strong>{demand.focus_area_code}</strong>
              {routedOnward ? ' and routed to DSAI' : ''}.
            </p>

            {/* Only demands past New carry an id, so this never shows in the
                audit queue. Ids are minted once and never re-minted (#821) —
                say so up front rather than letting the mismatch surprise. */}
            {demand.demand_id && (
              <p className={styles.hint} data-testid="route-slug-note">
                ID stays <strong>{demand.demand_id}</strong>.
              </p>
            )}

            {suggested && (
              <div className={styles.context} data-testid="route-suggestion">
                Suggested: <strong>{landingCodeLabel(suggested)}</strong>
                {demand.suggestion_overridden ? ' · Requestor chose another area.' : ''}
              </div>
            )}

            <Field label="Move it to" required>
              <RadioGroup
                className={styles.targets}
                value={target}
                onChange={(_, d) => setTarget(d.value)}
              >
                {FOCUS_AREAS.map((fa) => {
                  const isCurrent = fa.code === demand.focus_area_code && !routedOnward
                  return (
                    <Radio
                      key={fa.code}
                      value={fa.code}
                      // Choosing where it already is earns a 400; a disabled
                      // option says so before the round trip.
                      disabled={isCurrent}
                      label={
                        <span>
                          {fa.code} — {fa.name}
                          {isCurrent && <span className={styles.current}> (current)</span>}
                          {!isCurrent && fa.code === suggested && (
                            <span className={styles.suggested}> (suggested)</span>
                          )}
                        </span>
                      }
                    />
                  )
                })}
                <Radio
                  value={DIGITAL_COUNCIL_TARGET}
                  disabled={routedOnward}
                  label={
                    <span>
                      DSAI — not a DAC matter
                      {routedOnward && <span className={styles.current}> (current)</span>}
                      {!routedOnward && suggested === DIGITAL_COUNCIL_TARGET && (
                        <span className={styles.suggested}> (suggested)</span>
                      )}
                    </span>
                  }
                />
              </RadioGroup>
            </Field>

            {target === DIGITAL_COUNCIL_TARGET && (
              <Field
                label="DSAI cluster (optional)"
              >
                <Select
                  value={cluster}
                  onChange={(_, d) => setCluster(d.value)}
                  data-testid="route-dsai-cluster"
                >
                  <option value="">Not sure yet</option>
                  {clusters.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field
              label="Reason"
              required
            >
              <Textarea
                value={reason}
                onChange={(_, d) => setReason(d.value)}
                data-testid="route-reason"
                resize="vertical"
              />
            </Field>

            {target && (
              // Nobody told, so no channel: ToldLine's contract.
              <ToldLine channel={told ? 'email' : undefined}>{told || 'nobody'}</ToldLine>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={route.isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onSave}
              disabled={!canSave}
              data-testid="route-save"
            >
              {route.isPending ? 'Moving…' : 'Move'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
